/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import CardContent from '@mui/material/CardContent';
import dayjs from 'dayjs';
import { Button } from '@mui/material';
import Card from '@mui/material/Card';
import lodash from 'lodash';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import { apiHandleDownloadCSV } from '../ground-truth-updates.api';
import { actionTypes } from '../../../store/actions/base-actions';
// import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@mui/icons-material/Download';

function SessionInfoCard({
  session,
  adminUser,
  handleGroundTruthVerifiedFlagChange,
  expandedDetails = false
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [downloadFormat] = useState('csv');

  let actionRecognitionAccuracyPercentage = 100;
  let ballBouncePointAccuracyPercentage = 100;
  const {
    ground_truth_verified_timestamp: groundTruthVerifiedTimestamp = '',
    total_served: totalServed = 0,
    action_recognition_correction_counts: actionRecognitionCorrectionCounts = {},
    ball_bounce_point_correction_counts: ballBouncePointCorrectionCounts = {},
  } = session;

  if (groundTruthVerifiedTimestamp) {
    const actionRecognitionData = lodash.get(actionRecognitionCorrectionCounts, 'session_data', []);
    const actionRecognitionTotalCorrected = lodash.sumBy(actionRecognitionData, (item) => Object.values(item)[0]);
    const ballBouncePointData = lodash.get(ballBouncePointCorrectionCounts, 'session_data', []);
    const ballBouncePointTotalCorrected = lodash.sumBy(ballBouncePointData, (item) => Object.values(item)[0]);

    if (totalServed > 0) {
      actionRecognitionAccuracyPercentage = ((totalServed - actionRecognitionTotalCorrected) / totalServed) * 100;
      if (actionRecognitionAccuracyPercentage < 0) {
        actionRecognitionAccuracyPercentage = 0;
      }
      ballBouncePointAccuracyPercentage = ((totalServed - ballBouncePointTotalCorrected) / totalServed) * 100;
      if (ballBouncePointAccuracyPercentage < 0) {
        ballBouncePointAccuracyPercentage = 0;
      }
    }
  }

  const handleDownloadCSV = async () => {
    try {
      const sessionId = session.session_id;
      const sessionData = await apiHandleDownloadCSV({ sessionId, format: downloadFormat });
      const blob = new Blob([sessionData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `session_${sessionId}_data.${downloadFormat}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setTimeout(() => {
        dispatch({
          type: actionTypes.SHOW_NOTIFICATION,
          payload: {
            message: err.message || 'Download failed',
            severity: 'error',
            autoHideDuration: 3000,
          },
        });
      }, 10);
    }
  };

  return (
    <Card sx={{ minWidth: '350px', padding: '1rem' }} variant="outlined" key={session.session_id}>
      <Typography variant="h5" component="div">
        {session.session_id} &nbsp;
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button size="small" color="primary"
                onClick={() => navigate(`/session/${session.session_id}/details`)}>
          View Details
        </Button>
        {session.ground_truth_verified_timestamp ? (
          <Tooltip title={`last verified: ${session.ground_truth_verified_timestamp}`} arrow>
            <Chip label="Ground Truth Verified" color="primary" size="small"/>
          </Tooltip>) : (<Tooltip title="Ground Truth Not Verified" arrow>
          {/* <Chip label="Not Verified" color="warning" size="small"/> */}
          <Switch
            name={session.session_id}
            checked={Boolean(session.ground_truth_verified_timestamp)}
            onChange={handleGroundTruthVerifiedFlagChange}
            inputProps={{ 'aria-label': `session-${session.session_id}-label` }}
          />
        </Tooltip>)}
      </Box>
      {/* <CardActions>
        <Button size="small" color="primary"
                onClick={() => navigate(`/session/${session.session_id}/details`)}>
          View Details
        </Button>
      </CardActions> */}
      <CardContent>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
            {'Session Start Time'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {/* {dayjs(session.session_start_timestamp).format('DD, MMM YYYY hh:mm:ss A Z')} */}
            {dayjs(session.session_start_timestamp).tz(dayjs.tz.guess()).format('DD, MMM YYYY hh:mm:ss A')}
            {/* {dayjs(session.session_start_timestamp).utc().format('DD, MMM YYYY HH:mm:ss [UTC]')} */}
          </Typography>
        </Box>
        {
          <>
            <Box marginTop={2}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                {'Action Recognition Accuracy'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {groundTruthVerifiedTimestamp ? `${Math.round(actionRecognitionAccuracyPercentage)}%` : 'N/A'}
              </Typography>
            </Box>
            <Box marginTop={2}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                {'Ball Bounce Point Accuracy'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {groundTruthVerifiedTimestamp ? `${Math.round(ballBouncePointAccuracyPercentage)}%` : 'N/A'}
              </Typography>
            </Box>
          </>
        }
        {
          groundTruthVerifiedTimestamp
            ? (
              <Box marginTop={2}>
                <Collapse in={expandedDetails} timeout="auto" unmountOnExit>
                  <Box margin="1rem 0">
                    <Divider sx={{ margin: '1rem 0' }}/>
                    <Typography>{`Total deliveries: ${totalServed}`}</Typography>
                    <Typography>
                      {`action-recognition corrected: 
                      ${lodash.get(actionRecognitionCorrectionCounts, 'corrected_record_count', 0)}`}
                    </Typography>
                    <Typography>
                      {`ball-bounce-point corrected: 
                      ${lodash.get(ballBouncePointCorrectionCounts, 'corrected_record_count', 0)}`}
                    </Typography>
                    <Divider sx={{ margin: '1rem 0' }}/>
                    <Typography variant="h6" sx={{ whiteSpace: 'pre-line', marginBottom: '0.5rem' }}>
                      {'action-recognition classifications\ncorrected'}
                    </Typography>
                    {lodash.get(actionRecognitionCorrectionCounts, 'session_data', []).map((item, index) => (
                      Object.entries(item).map(([key, value]) => (
                        <Typography key={`${key}-${index}`}>{`${key}: ${value}`}</Typography>
                      ))
                    ))}
                  </Box>
                </Collapse>
                {
                  adminUser ? (
                    <Box width="100%" display="flex" justifyContent="flex-end" alignItems="center">
                      {/* <Tooltip title="Download CSV file" arrow>
                    <IconButton onClick={handleDownloadCSV} sx={{ marginRight: '0rem' }} aria-label="download csv">
                      <DownloadIcon/>
                    </IconButton>
                  </Tooltip> */}
                      <Button
                        size="small"
                        variant="text"
                        onClick={handleDownloadCSV}
                        endIcon={<DownloadIcon/>}
                        aria-label="download csv"
                        sx={{ marginRight: '0rem', fontSize: '12px' }}
                      >
                        Download CSV
                      </Button>
                    </Box>
                  ) : null
                }
              </Box>
              ) : null
        }
      </CardContent>
    </Card>
  );
}

export default React.memo(SessionInfoCard);
